import { useEffect } from "react";

/**
 * SEOHead — updates document.title and key meta tags on route change.
 * Avoids react-helmet dependency (lightweight effect-based approach).
 *
 * Props:
 *  - title           full <title>
 *  - description     meta description
 *  - keywords        comma-separated meta keywords
 *  - lang            html lang (e.g. "nl", "tr", "en")
 *  - canonical       canonical absolute URL
 *  - ogTitle / ogDescription / ogImage / ogLocale
 *  - alternates      [{ hreflang, href }] for cross-language SEO
 *  - jsonLd          object — appends a <script type="application/ld+json">
 */
const upsertMeta = (selector, attr, attrValue, content) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
};

const upsertLink = (rel, hreflang, href) => {
  const sel = hreflang
    ? `link[rel="alternate"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
};

const SEO_TAG_ID = "seo-jsonld-dynamic";

const SEOHead = ({
  title,
  description,
  keywords,
  lang = "nl",
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogLocale,
  alternates = [],
  jsonLd,
}) => {
  useEffect(() => {
    const prevTitle = document.title;
    const prevHtmlLang = document.documentElement.getAttribute("lang");

    if (title) document.title = title;
    if (lang) document.documentElement.setAttribute("lang", lang);

    if (description) upsertMeta('meta[name="description"]', "name", "description", description);
    if (keywords) upsertMeta('meta[name="keywords"]', "name", "keywords", keywords);

    if (ogTitle || title)
      upsertMeta('meta[property="og:title"]', "property", "og:title", ogTitle || title);
    if (ogDescription || description)
      upsertMeta(
        'meta[property="og:description"]',
        "property",
        "og:description",
        ogDescription || description
      );
    if (ogImage)
      upsertMeta('meta[property="og:image"]', "property", "og:image", ogImage);
    if (ogLocale)
      upsertMeta('meta[property="og:locale"]', "property", "og:locale", ogLocale);
    if (canonical) {
      upsertLink("canonical", null, canonical);
      upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
    }

    // hreflang alternates
    alternates.forEach((a) => {
      upsertLink("alternate", a.hreflang, a.href);
    });

    // JSON-LD injection (replace previous dynamic block)
    const prev = document.getElementById(SEO_TAG_ID);
    if (prev) prev.remove();
    if (jsonLd) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = SEO_TAG_ID;
      s.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }

    return () => {
      // restore base values on unmount (best-effort)
      document.title = prevTitle;
      if (prevHtmlLang) document.documentElement.setAttribute("lang", prevHtmlLang);
      const cleanup = document.getElementById(SEO_TAG_ID);
      if (cleanup) cleanup.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title,
    description,
    keywords,
    lang,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogLocale,
    JSON.stringify(alternates),
    JSON.stringify(jsonLd),
  ]);

  return null;
};

export default SEOHead;
